/*
 * ANANDA Benefits — interactions.
 *
 * Two independent behaviours, layered onto separate nested elements so
 * they never fight each other (see ananda-benefits.css):
 *
 *  1) SCROLL ENTER/EXIT  (all devices, scrubbed to scroll via Motion):
 *     As the section passes through the viewport, each item flies up from
 *     below + fades + scales in, holds while centred, then flies out the
 *     top — staggered per item. Fully driven by the user's scroll.
 *     Applied to [data-ab-scroll].
 *
 *  2) MAGNETIC 3D TILT  (hover + fine-pointer devices only):
 *     Each icon tilts toward the cursor and shifts slightly, by an amount
 *     scaled to its --depth, giving a real 3D parallax. rAF-lerped so it
 *     glides. Applied to [data-ab-tilt]. Touch devices skip this and keep
 *     the perpetual CSS bob + scroll reveal.
 *
 * Uses the Motion library (window.Motion) we self-host on the theme.
 * Degrades gracefully: with no JS/Motion the items are simply visible.
 */
(function () {
  function ready(cb) {
    if (window.Motion && window.Motion.scroll) return cb();
    var n = 0;
    var iv = setInterval(function () {
      if (window.Motion && window.Motion.scroll) { clearInterval(iv); cb(); }
      else if (++n > 60) { clearInterval(iv); /* CSS keeps items visible */ }
    }, 50);
  }

  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
  function smooth(t) { return t * t * (3 - 2 * t); } // smoothstep ease

  // Fling little snow specks off the THREE UPPER BRANCH JUNCTIONS of the
  // snowflake — the little "cross" points a bit down each arm where the feather
  // branches meet, where snow would realistically catch — rather than from the
  // bare tips or the centre. Each junction's outward direction (unit vector from
  // the icon centre toward it) is the base; we fan each speck around it, pop it
  // up-and-out to a peak, then let gravity carry it down. All motion is CSS
  // (custom props below); each span self-removes on animationend. The --delay
  // lines the burst up with the big "shake" beat of anandaSnowShake.
  // Junction coords come from the SVG (viewBox 64x64) as % of the icon box:
  //   top arm (32,19.5) -> 50%/30.5 · upper-left (21.4,26) -> 33.4%/40.6 · upper-right (42.6,26) -> 66.6%/40.6
  function spawnSnow(host) {
    var TIPS = [
      { ox: 50,   oy: 30.5, dx: 0,      dy: -1 },     // top arm — branch junction
      { ox: 33.4, oy: 40.6, dx: -0.87,  dy: -0.49 },  // upper-left arm — branch junction
      { ox: 66.6, oy: 40.6, dx: 0.87,   dy: -0.49 }   // upper-right arm — branch junction
    ];
    var PER = 5;
    for (var t = 0; t < TIPS.length; t++) {
      var tip = TIPS[t];
      for (var j = 0; j < PER; j++) {
        var s = document.createElement('span');
        s.className = 'ananda-benefits__flake';
        // fan the speck around the spike's outward direction (~±40deg)
        var a = (Math.random() * 2 - 1) * 0.7;
        var cs = Math.cos(a), sn = Math.sin(a);
        var dirx = tip.dx * cs - tip.dy * sn;
        var diry = tip.dx * sn + tip.dy * cs;
        var pk = 9 + Math.random() * 15;                       // peak distance (up & out)
        var px = dirx * pk;
        var py = diry * pk - Math.random() * 5;
        var fall = 40 + Math.random() * 48;                    // gravity carry downward
        var dx = px + dirx * (4 + Math.random() * 10);         // keeps drifting outward
        var dy = py + fall;                                    // net downward past the spike
        var size = 4 + Math.random() * 5;
        var rot = (Math.random() * 2 - 1) * 240;
        var delay = 0.24 + Math.random() * 0.16;
        var dur = 0.62 + Math.random() * 0.4;
        s.style.cssText =
          '--ox:' + tip.ox + '%;--oy:' + tip.oy + '%;' +
          '--px:' + px.toFixed(1) + 'px;--py:' + py.toFixed(1) + 'px;' +
          '--dx:' + dx.toFixed(1) + 'px;--dy:' + dy.toFixed(1) + 'px;' +
          '--size:' + size.toFixed(1) + 'px;--rot:' + rot.toFixed(0) + 'deg;' +
          '--delay:' + delay.toFixed(2) + 's;--dur:' + dur.toFixed(2) + 's;';
        s.addEventListener('animationend', function () {
          if (this.parentNode) this.parentNode.removeChild(this);
        });
        host.appendChild(s);
      }
    }
  }

  // Pour liquid over the rim when the cocktail glass tips. Built to read as a
  // connected STREAM, not loose droplets: two overlapping "ribbon" spans that
  // grow down from the rim form the body of the pour, and a dense, tightly-spread
  // set of elongated break-off drips falls down the same line for texture. All
  // emit from the same rim point (--ox/--oy), nudged a few px left to the glass's
  // tilted rim edge. Soft translucent red (--accent), like the snow. Each span
  // self-removes on animationend.
  var POUR_OX = '11%', POUR_OY = '22%';
  function spawnPour(host) {
    // 1) connected ribbons — the visible body of the stream
    var RIB = [
      { w: 5.4, h: 56, sx: -3, sx2: -7, delay: 0.32, dur: 1.08 },
      { w: 3.6, h: 47, sx: -5, sx2: -9, delay: 0.40, dur: 0.96 }
    ];
    for (var r = 0; r < RIB.length; r++) {
      var rb = RIB[r];
      var p = document.createElement('span');
      p.className = 'ananda-benefits__pour';
      p.style.cssText =
        '--ox:' + POUR_OX + ';--oy:' + POUR_OY + ';' +
        '--w:' + rb.w + 'px;--h:' + rb.h + 'px;' +
        '--sx:' + rb.sx + 'px;--sx2:' + rb.sx2 + 'px;' +
        '--delay:' + rb.delay + 's;--dur:' + rb.dur + 's;';
      p.addEventListener('animationend', function () {
        if (this.parentNode) this.parentNode.removeChild(this);
      });
      host.appendChild(p);
    }
    // 2) break-off drips — tight + dense so they read as part of the stream
    var N = 16;
    for (var k = 0; k < N; k++) {
      var s = document.createElement('span');
      s.className = 'ananda-benefits__drip';
      var px = -(1 + Math.random() * 4);                 // tight left lean
      var py = 2 + Math.random() * 6;
      var dx = px - (1 + Math.random() * 5);
      var dy = 58 + Math.random() * 52;                  // long fall
      var size = 3.5 + Math.random() * 2.5;
      var delay = 0.36 + Math.random() * 0.52;           // continuous over the pour
      var dur = 0.7 + Math.random() * 0.45;
      s.style.cssText =
        '--ox:' + POUR_OX + ';--oy:' + POUR_OY + ';' +
        '--px:' + px.toFixed(1) + 'px;--py:' + py.toFixed(1) + 'px;' +
        '--dx:' + dx.toFixed(1) + 'px;--dy:' + dy.toFixed(1) + 'px;' +
        '--size:' + size.toFixed(1) + 'px;--delay:' + delay.toFixed(2) + 's;--dur:' + dur.toFixed(2) + 's;';
      s.addEventListener('animationend', function () {
        if (this.parentNode) this.parentNode.removeChild(this);
      });
      host.appendChild(s);
    }
  }

  // Flip the beach-umbrella icon to/from the park-bench face — a horizontal
  // card-flip toggle. We turn the .flip container edge-on (scaleX via a CSS
  // transition), swap the visible face at the edge, then turn it face-on again.
  // Driven by transitionend so it self-paces, with setTimeout safety nets so a
  // missed transitionend (e.g. a backgrounded tab) can never leave the icon
  // stuck edge-on/invisible. Clears the busy flag when fully done.
  function flipIcon(iconWrap) {
    var flip = iconWrap.querySelector('[data-ab-flip]');
    if (!flip) { iconWrap.dataset.abBusy = ''; return; }
    var swapped = false, finished = false, t1, t2;
    function swap() {
      if (swapped) return;
      swapped = true;
      var n = flip.querySelectorAll('.ananda-benefits__face').length || 1;
      var cur = parseInt(flip.getAttribute('data-ab-face') || '0', 10) || 0;
      flip.setAttribute('data-ab-face', String((cur + 1) % n));  // umbrella → bench → DJ → …
      flip.classList.remove('is-flipping');       // turn face-on again
    }
    function finish() {
      if (finished) return;
      finished = true;
      clearTimeout(t1); clearTimeout(t2);
      flip.removeEventListener('transitionend', onEnd);
      swap();                                      // guarantees a valid end state
      iconWrap.dataset.abBusy = '';
    }
    var onEnd = function (e) {
      if (e.target !== flip || e.propertyName !== 'transform') return;  // ignore face opacity
      if (!swapped) swap(); else finish();
    };
    flip.addEventListener('transitionend', onEnd);
    requestAnimationFrame(function () { flip.classList.add('is-flipping'); });  // turn edge-on
    t1 = setTimeout(swap, 400);                    // safety: ensure the swap happens
    t2 = setTimeout(finish, 800);                  // safety: ensure we finish + un-busy
  }

  // Progress of a section across ['start end','end start']:
  // 0 = section top at viewport bottom (about to enter),
  // 1 = section bottom at viewport top (just left).
  function progressOf(section) {
    var r = section.getBoundingClientRect();
    var vh = window.innerHeight || 1;
    return clamp((vh - r.top) / (r.height + vh), 0, 1);
  }

  function init() {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var sections = document.querySelectorAll('[data-ananda-benefits]');
    if (!sections.length) return;

    sections.forEach(function (section) {
      if (section.dataset.abInit === '1') return;
      section.dataset.abInit = '1';

      var items = Array.prototype.slice.call(
        section.querySelectorAll('[data-ananda-benefits-item]')
      );
      if (!items.length) return;

      var scrolls = items.map(function (it) { return it.querySelector('[data-ab-scroll]'); });
      var tilts   = items.map(function (it) { return it.querySelector('[data-ab-tilt]'); });
      var depths  = items.map(function (it) {
        return parseFloat(getComputedStyle(it).getPropertyValue('--depth')) || 1;
      });
      var n = items.length;

      // ---------- 1) Scroll enter/exit (rAF-smoothed scrub) ----------
      // Scroll feeds a TARGET progress; a rAF loop eases the CURRENT
      // progress toward it every frame so the reveal glides BETWEEN scroll
      // steps instead of jumping a chunk per mouse-wheel notch — that
      // stepping is what made the first fly-up look choppy. Mirrors the
      // lerp the tilt below uses. The loop idles itself once it catches up.
      if (!reduce) {
        var ENTER = 96;   // px below resting at entry
        var EXIT  = -96;  // px above resting at exit

        var render = function (p) {
          for (var i = 0; i < n; i++) {
            var es = 0.02 + i * 0.05, ee = es + 0.26;   // enter window
            var xs = 0.66 + i * 0.05, xe = xs + 0.26;   // exit window
            var e = smooth(clamp((p - es) / (ee - es), 0, 1));
            var x = smooth(clamp((p - xs) / (xe - xs), 0, 1));
            var ty = (1 - e) * ENTER + x * EXIT;
            var op = e * (1 - x);
            var sc = 0.82 + 0.18 * e - 0.18 * x;
            var el = scrolls[i];
            if (el) {
              el.style.opacity = op.toFixed(3);
              el.style.transform = 'translateY(' + ty.toFixed(1) + 'px) scale(' + sc.toFixed(3) + ')';
            }
          }
        };

        var targetP = progressOf(section);
        var curP = targetP;
        var sRaf = null;

        var sTick = function () {
          curP += (targetP - curP) * 0.16;       // lerp: higher = tighter to scroll
          if (Math.abs(targetP - curP) < 0.0004) {
            curP = targetP; sRaf = null; render(curP); return;
          }
          render(curP);
          sRaf = requestAnimationFrame(sTick);
        };
        var sKick = function () { if (!sRaf) sRaf = requestAnimationFrame(sTick); };

        // Correct initial state before first paint (no flash).
        render(curP);

        if (window.Motion && window.Motion.scroll) {
          window.Motion.scroll(function (p) { targetP = p; sKick(); }, {
            target: section,
            offset: ['start end', 'end start']
          });
        } else {
          window.addEventListener('scroll', function () {
            targetP = progressOf(section); sKick();
          }, { passive: true });
        }
      }

      // ---------- 2) Magnetic 3D tilt (hover + fine pointer) ----------
      var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (!reduce && canHover) {
        var ROT = 13, SHIFT = 13;
        var px = 0, py = 0, active = false, raf = null;
        var cur = items.map(function () { return { rx: 0, ry: 0, tx: 0, ty: 0 }; });

        var tick = function () {
          var moving = false;
          for (var i = 0; i < n; i++) {
            var trx = 0, tryy = 0, ttx = 0, tty = 0;
            if (active) {
              var r = items[i].getBoundingClientRect();
              var dx = clamp((px - (r.left + r.width / 2)) / (r.width || 1), -1.4, 1.4);
              var dy = clamp((py - (r.top + r.height / 2)) / (r.height || 1), -1.4, 1.4);
              tryy = dx * ROT;
              trx  = -dy * ROT;
              ttx  = dx * SHIFT * depths[i];
              tty  = dy * SHIFT * depths[i];
            }
            var c = cur[i];
            c.rx += (trx - c.rx) * 0.12;
            c.ry += (tryy - c.ry) * 0.12;
            c.tx += (ttx - c.tx) * 0.12;
            c.ty += (tty - c.ty) * 0.12;
            if (Math.abs(trx - c.rx) + Math.abs(tryy - c.ry) +
                Math.abs(ttx - c.tx) + Math.abs(tty - c.ty) > 0.05) moving = true;
            tilts[i].style.transform =
              'rotateX(' + c.rx.toFixed(2) + 'deg) rotateY(' + c.ry.toFixed(2) + 'deg) ' +
              'translate3d(' + c.tx.toFixed(1) + 'px,' + c.ty.toFixed(1) + 'px,0)';
          }
          raf = (moving || active) ? requestAnimationFrame(tick) : null;
        };

        section.addEventListener('pointermove', function (ev) {
          px = ev.clientX; py = ev.clientY; active = true;
          if (!raf) raf = requestAnimationFrame(tick);
        }, { passive: true });

        section.addEventListener('pointerleave', function () {
          active = false;
          if (!raf) raf = requestAnimationFrame(tick);
        }, { passive: true });
      }

      // ---------- 3) Click gesture (per icon type) ----------
      // Each icon answers a click with its own themed animation, kept on the
      // <svg> (a free transform layer) so it never fights the bob / tilt /
      // scroll transforms above it. We add one type at a time, dispatched by the
      // icon's data-ab-icon value. A busy-guard ignores re-clicks mid-cycle.
      //   snowflake → cold shiver that builds into shakes which throw the snow off
      //   martini   → glass lifts, tips and pours a little liquid over the rim
      //   umbrella  → card-flips through faces: umbrella → park bench → DJ party → home
      //   bottle    → "cut" along the diagonal: the top shard slides off & the colour reddens, then reverses
      if (!reduce) {
        var GESTURES = {
          snowflake: { cls: 'is-shaking', spawn: spawnSnow },
          martini:   { cls: 'is-pouring', spawn: spawnPour },
          umbrella:  { flip: true },
          bottle:    { cls: 'is-cracking' }
        };
        items.forEach(function (it) {
          var iconWrap = it.querySelector('[data-ab-icon]');
          if (!iconWrap) return;
          var g = GESTURES[iconWrap.getAttribute('data-ab-icon')];
          if (!g) return;                                  // other icons get theirs later
          iconWrap.style.cursor = 'pointer';
          iconWrap.addEventListener('click', function () {
            if (iconWrap.dataset.abBusy === '1') return;
            iconWrap.dataset.abBusy = '1';
            if (g.flip) { flipIcon(iconWrap); return; }    // flip clears its own busy flag
            var svg = iconWrap.querySelector('svg');
            if (g.spawn) g.spawn(iconWrap);                // particle gestures only
            iconWrap.classList.add(g.cls);
            var done = function () {
              iconWrap.classList.remove(g.cls);
              iconWrap.dataset.abBusy = '';
              if (svg) svg.removeEventListener('animationend', done);
            };
            if (svg) svg.addEventListener('animationend', done);
            else setTimeout(done, 1400);
          });
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { ready(init); });
  } else {
    ready(init);
  }
})();
